/** Cópia de PacProBackend/src/modules/app-versao/domain/notas-markdown.ts. Mudou lá, mude aqui, no backoffice e no app. */

export interface Trecho {
  texto: string;
  negrito: boolean;
  italico: boolean;
  /** Só `http` ou `https`. Link com outro esquema vira texto comum. */
  link: string | null;
}

export type BlocoNotas =
  | { tipo: 'titulo'; trechos: Trecho[] }
  | { tipo: 'paragrafo'; linhas: Trecho[][] }
  | { tipo: 'lista'; ordenada: boolean; inicio: number; itens: Trecho[][] };

type Lista = Extract<BlocoNotas, { tipo: 'lista' }>;
type Estilo = Pick<Trecho, 'negrito' | 'italico' | 'link'>;

const TITULO = /^#{1,6}\s+(.*?)(?:\s+#+)?$/;
const ITEM = /^[-*+•]\s+(.*)$/;
const ITEM_NUMERADO = /^(\d{1,4})[.)]\s+(.*)$/;
const LINK_PERMITIDO = /^https?:\/\/\S+$/i;
const SEM_ESTILO: Estilo = { negrito: false, italico: false, link: null };

/**
 * Grupos: 1 caractere escapado; 2 e 3 negrito (`**` e `__`); 4 e 5 itálico (`*` e `_`); 6 e 7 texto
 * e endereço do link. O `_` só marca fora de palavra (`nome_do_arquivo` fica como está), e nenhuma
 * marcação abre ou fecha encostada em espaço (`2 * 3 * 4` fica como está).
 *
 * Para negrito e itálico se misturarem sem sobrar asterisco (`*itálico **negrito** dentro*`), o
 * itálico de um caractere não abre nem fecha encostado em outro igual a ele, a não ser que seja o
 * terceiro de um `***` (ou `___`) que também abre ou fecha um negrito: `***a***`,
 * `***negrito** itálico*` e `*itálico **negrito***`. Pelo mesmo motivo o negrito não abre antes de
 * um terceiro `*` ou `_` e não fecha antes de um terceiro `*` ou `_` (em `**negrito *itálico***` o
 * itálico fecha primeiro). O texto do link não tem colchete: em link dentro de link vale o de dentro.
 */
const INLINE = new RegExp(
  [
    String.raw`\\([\\*_[\]()#+.!-])`,
    String.raw`\*\*(?![\s*])(.+?)(?<!\s)\*\*(?!\*)`,
    String.raw`(?<![\p{L}\p{N}_])__(?![\s_])(.+?)(?<!\s)__(?![\p{L}\p{N}_])`,
    String.raw`(?<!\*)\*(?:(?![\s*])|(?=\*\*[^\s*]))(.+?)(?:(?<![\s*])|(?<=[^\s*]\*\*))\*(?!\*)`,
    String.raw`(?<![\p{L}\p{N}_])_(?:(?![\s_])|(?=__[^\s_]))(.+?)(?:(?<![\s_])|(?<=[^\s_]__))_(?![\p{L}\p{N}_])`,
    String.raw`\[([^[\]]+)\]\(([^)\s]+)\)`,
  ].join('|'),
  'gu',
);

export function lerNotasMarkdown(markdown: string | null | undefined): BlocoNotas[] {
  return lerBlocos(markdown).map(({ bloco }) => bloco);
}

/** Cada bloco com a marca de que veio depois de uma linha em branco (o texto puro mantém essa linha). */
function lerBlocos(markdown: string | null | undefined): { bloco: BlocoNotas; depoisDeLinhaEmBranco: boolean }[] {
  const lidos: { bloco: BlocoNotas; depoisDeLinhaEmBranco: boolean }[] = [];
  let paragrafo: Trecho[][] | null = null;
  let lista: Lista | null = null;
  let linhaEmBranco = false;
  const abrir = (bloco: BlocoNotas) => {
    lidos.push({ bloco, depoisDeLinhaEmBranco: linhaEmBranco });
    linhaEmBranco = false;
  };

  for (const bruta of (markdown ?? '').replace(/\r\n?/g, '\n').split('\n')) {
    const linha = bruta.trim();
    if (!linha) {
      paragrafo = null;
      lista = null;
      linhaEmBranco = true;
      continue;
    }

    const titulo = TITULO.exec(linha);
    if (titulo) {
      paragrafo = null;
      lista = null;
      const trechos = lerTrechos(titulo[1]);
      if (trechos.length) abrir({ tipo: 'titulo', trechos });
      continue;
    }

    const numerado = ITEM_NUMERADO.exec(linha);
    const item = numerado ? null : ITEM.exec(linha);
    if (numerado || item) {
      paragrafo = null;
      const ordenada = numerado !== null;
      const trechos = lerTrechos(numerado ? numerado[2] : (item as RegExpExecArray)[1]);
      if (!trechos.length) continue;
      if (lista === null || lista.ordenada !== ordenada) {
        lista = { tipo: 'lista', ordenada, inicio: numerado ? Number(numerado[1]) : 1, itens: [] };
        abrir(lista);
      }
      lista.itens.push(trechos);
      continue;
    }

    lista = null;
    if (paragrafo === null) {
      paragrafo = [];
      abrir({ tipo: 'paragrafo', linhas: paragrafo });
    }
    paragrafo.push(lerTrechos(linha));
  }

  return lidos;
}

export function lerTrechos(texto: string, estilo: Estilo = SEM_ESTILO): Trecho[] {
  const trechos: Trecho[] = [];
  let inicio = 0;
  for (const achado of texto.matchAll(INLINE)) {
    const posicao = achado.index ?? 0;
    juntar(trechos, texto.slice(inicio, posicao), estilo);
    inicio = posicao + achado[0].length;
    const [, escapado, negrito1, negrito2, italico1, italico2, rotulo, endereco] = achado;
    if (escapado !== undefined) {
      juntar(trechos, escapado, estilo);
    } else if (negrito1 !== undefined || negrito2 !== undefined) {
      juntarTodos(trechos, lerTrechos(negrito1 ?? negrito2, { ...estilo, negrito: true }));
    } else if (italico1 !== undefined || italico2 !== undefined) {
      juntarTodos(trechos, lerTrechos(italico1 ?? italico2, { ...estilo, italico: true }));
    } else {
      const permitido = estilo.link === null && LINK_PERMITIDO.test(endereco);
      juntarTodos(trechos, lerTrechos(rotulo, permitido ? { ...estilo, link: endereco } : estilo));
    }
  }
  juntar(trechos, texto.slice(inicio), estilo);
  return trechos;
}

function juntar(trechos: Trecho[], texto: string, estilo: Estilo): void {
  if (!texto) return;
  const ultimo = trechos[trechos.length - 1];
  if (ultimo && ultimo.negrito === estilo.negrito && ultimo.italico === estilo.italico && ultimo.link === estilo.link) {
    ultimo.texto += texto;
  } else {
    trechos.push({ texto, negrito: estilo.negrito, italico: estilo.italico, link: estilo.link });
  }
}

function juntarTodos(trechos: Trecho[], novos: Trecho[]): void {
  for (const novo of novos) juntar(trechos, novo.texto, novo);
}

const textoDosTrechos = (trechos: Trecho[]) => trechos.map((trecho) => trecho.texto).join('');

/**
 * O texto que o app antigo mostra e que a coluna `notas` guarda, sem marcação nenhuma: título em linha
 * solta, item com `•`, numerada com o número. Linha em branco só entre blocos e nunca duas seguidas:
 * uma antes de todo título (menos o primeiro bloco) e uma onde o Markdown tinha linha em branco entre
 * dois blocos (várias seguidas contam como uma). Assim a nota antiga com parágrafos faz ida e volta
 * (`markdownDeTextoAntigo` e de novo `textoPuroDasNotas` devolvem o mesmo texto).
 */
export function textoPuroDasNotas(markdown: string | null | undefined): string {
  const linhas: string[] = [];
  for (const { bloco, depoisDeLinhaEmBranco } of lerBlocos(markdown)) {
    if (linhas.length && (bloco.tipo === 'titulo' || depoisDeLinhaEmBranco)) linhas.push('');
    if (bloco.tipo === 'titulo') {
      linhas.push(textoDosTrechos(bloco.trechos));
    } else if (bloco.tipo === 'paragrafo') {
      for (const linha of bloco.linhas) linhas.push(textoDosTrechos(linha));
    } else {
      bloco.itens.forEach((item, indice) =>
        linhas.push(`${bloco.ordenada ? `${bloco.inicio + indice}.` : '•'} ${textoDosTrechos(item)}`),
      );
    }
  }
  return linhas.join('\n').trim();
}

/** Uma linha só, para a coluna "O que mudou" do histórico. */
export function resumoDasNotas(textoPuro: string | null | undefined): string {
  return (textoPuro ?? '')
    .split('\n')
    .map((linha) => linha.replace(/^•\s*/, '').trim())
    .filter(Boolean)
    .join(' · ');
}

/** Notas gravadas antes do Markdown, abertas no editor: o `•` vira o marcador de lista. */
export function markdownDeTextoAntigo(notas: string | null | undefined): string {
  return (notas ?? '')
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((linha) => linha.replace(/^(\s*)•\s*/, '$1- '))
    .join('\n')
    .trim();
}

/**
 * O Markdown só vale enquanto `notas` for o texto puro derivado dele. Se `notas` foi regravado
 * por fora (os antigos scripts `lancar-*.sh`; hoje a versão se anuncia no backoffice, tela
 * Versões), quem manda é o texto dele.
 */
export function notasMarkdownValidas(notas: string | null, markdown: string | null): string | null {
  if (markdown === null || notas === null) return null;
  return textoPuroDasNotas(markdown) === notas ? markdown : null;
}
